import styled, { css } from 'styled-components';
import { hoverFocus, margin, media, padding } from '../../theme';
import { ButtonVariant } from './button';
export const Button = styled.button `
  box-shadow: ${(props) => props.theme.shadows.button};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  width: 100%;
  min-width: 9.375rem;
  height: ${(props) => props.theme.buttons.buttonHeight};

  ${padding.horizontal('normal')}

  font-family: ${(props) => props.theme.fonts.stacks.secondary};
  text-align: center;
  text-decoration: none;

  transition: color ${(props) => props.theme.transitions.default},
    background-color ${(props) => props.theme.transitions.default},
    border-color ${(props) => props.theme.transitions.default};

  /* Make sure text doesn't overflow */
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  /* Adds an invisible outline to buttons without borders, so then equal in height with buttons that have borders */
  border-width: 0.125rem;
  border-style: solid;
  border-color: transparent;
  border-radius: ${(props) => props.theme.buttons.borderRadius};
  font-weight: ${(props) => props.theme.buttons.fontWeight};
  ${(props) => props.theme.buttons.fontSizeMobile && `font-size: ${props.theme.buttons.fontSizeMobile};`}
  ${media.up('sm') `
    // Cancel the 'width: 100%' for mobile
    width: auto;
    ${css `
        ${(props) => props.theme.buttons.fontSize && `font-size: ${props.theme.buttons.fontSize};`}
      `}
    ${padding.horizontal('medium')}
  `}

  /* If an icon is included, add some spacing between the icon and button text */
  .svg-icon {
    ${({ theme }) => theme.buttons.defaultIconMargin && `margin: ${theme.buttons.defaultIconMargin};`}
  }

  /* Two buttons next to each other get some spacing between them */
  & + & {
    ${media.down('xs') `
      ${margin.top('smaller')};
    `}

    ${media.up('sm') `
      ${margin.left('smaller')};
    `}
  }

  /* Button variations, only ONE of these applies at the time. */
  ${({ variant }) => variant === ButtonVariant.PRIMARY && primaryButtonStyles}
  ${({ variant }) => variant === ButtonVariant.SECONDARY && secondaryButtonStyles}
  ${({ variant }) => variant === ButtonVariant.DARK && darkButtonStyles}
  ${({ variant }) => variant === ButtonVariant.LIGHT && lightButtonStyles}
  ${({ variant }) => variant === ButtonVariant.LINK && linkButtonStyles}
  ${({ variant }) => variant === ButtonVariant.SELECTED && selectedButtonStyles}

  /* Button options */
  ${({ isOutlined }) => isOutlined && outlinedButtonStyles}
  ${({ isRounded }) => isRounded && roundedButtonStyles}
  ${({ isLoading }) => isLoading && loadingButtonStyles}
  ${({ isFullWidth }) => isFullWidth && fullWidthButtonStyles}
  ${({ isLeftAligned }) => isLeftAligned && leftAlignedButtonStyles}

  /* WS10 only */
  ${({ isSmall }) => isSmall && smallStyles}
  ${({ isNotBordered }) => isNotBordered && notBorderedButtonStyles}

  /* Disabled style must overrule all other styles, so keep it at the bottom please */
  ${({ isDisabled }) => isDisabled && disabledButtonStyles}
`;
/**
 * Button options
 */
const roundedButtonStyles = css `
  border-radius: 1.5625rem;
  border-width: 0.125rem; // equals a 2px border, thicker than normal
  border-style: solid;

  font-weight: ${(props) => props.theme.fonts.weights.bold};

  /* Because the border became 1px thicker, the button padding must become smaller to
    accomodate for the increased height */
  padding-top: 0.6875rem;
  padding-bottom: 0.6875rem;
`;
const outlinedButtonStyles = css `
  border-color: inherit;

  ${({ variant, theme }) => variant === ButtonVariant.PRIMARY &&
    css `
      background-color: transparent;
      color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};

      ${hoverFocus `
        background-color: ${theme.colors.primaryDark};
        border-color: ${theme.colors.primaryDark};
        color: ${theme.colors.white};
      `}
    `}

  ${({ variant, theme }) => variant === ButtonVariant.SECONDARY &&
    css `
      background-color: transparent;
      color: ${theme.colors.grayD10};
      border-color: ${theme.colors.grayD10};

      ${hoverFocus `
        background-color: ${theme.colors.grayD10};
        border-color: ${theme.colors.grayD10};
        color: ${theme.colors.white};
      `}
    `}

  ${({ variant, theme }) => variant === ButtonVariant.DARK &&
    css `
      background-color: transparent;
      color: ${theme.colors.grayD40};
      border-color: ${theme.colors.grayD40};

      /* Here the default bold weight is cancelled as this style looks better with regular weight */
      font-weight: ${theme.fonts.weights.regular};

      ${hoverFocus `
        background-color: ${theme.colors.grayD40};
        border-color: ${theme.colors.grayD40};
        color: ${theme.colors.white};
      `}
    `}

  ${({ variant, theme }) => variant === ButtonVariant.LIGHT &&
    css `
      background-color: transparent;
      color: ${theme.colors.white};
      border-color: ${theme.colors.white};

      ${hoverFocus `
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.white};
        color: ${theme.colors.grayD30};
      `}
    `}
`;
const loadingButtonStyles = css `
  position: relative;
  opacity: 0.5;
  background-color: ${(props) => props.theme.colors.grayD40};
  border-color: transparent;
  position: relative;
  cursor: default;

  /* Remove the padding because the Loader component is exactly the height of a regular button */
  ${padding.vertical('none')}

  ${(props) => hoverFocus `
    background-color: ${props.theme.colors.grayD40};
    border-color: transparent;
  `}

  .spinner {
    /* Height of the spinner is decreased slightly so it accommodates for the border of the button */
    width: ${(props) => props.theme.buttons.loadingSpinnerSize};
    height: ${(props) => props.theme.buttons.loadingSpinnerSize};
  }
`;
/** Applies a full width to the button even when not on mobile  */
const fullWidthButtonStyles = css `
  width: 100%;

  ${media.up('sm') `
    width: 100%;
  `}
`;
const leftAlignedButtonStyles = css `
  justify-content: space-between;
  .svg-icon {
    ${(props) => props.theme.buttons.rightIconMargin && `margin: ${props.theme.buttons.rightIconMargin};`}
  }
`;
const notBorderedButtonStyles = css `
  border-width: 0;
`;
const smallStyles = css `
  width: auto;
  min-width: 0;
  height: ${(props) => props.theme.buttons.smallButtonHeight};

  ${padding.horizontal('normal')}

  font-size: ${(props) => props.theme.buttons.smallButtonFontSizeMobile};
  font-weight: ${(props) => props.theme.fonts.weights.regular};

  ${(props) => media.up('sm') `
    font-size: ${props.theme.buttons.smallButtonFontSize};
    ${padding.horizontal('normal')}
  `}
`;
const disabledButtonStyles = css `
  background-color: ${(props) => props.disabledBackgroundColor ? props.disabledBackgroundColor : props.theme.colors.grayL30};
  color: ${(props) => props.theme.colors.white};
  cursor: not-allowed;
  border-color: transparent;

  ${(props) => hoverFocus `
    background-color: ${props.disabledBackgroundColor ? props.disabledBackgroundColor : props.theme.colors.grayL30};
  `}

  ${({ variant, theme }) => variant === ButtonVariant.LINK &&
    css `
      background-color: transparent;
      color: ${theme.colors.grayL20};

      ${hoverFocus `
        background-color: transparent;
      `}
    `}
`;
// ---------
/**
 * Button Variations
 */
const primaryButtonStyles = css `
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.buttons.textColor};

  ${(props) => hoverFocus `
    background-color: ${props.theme.buttons.primary.hoverFocusBackgroundColor};
    color: ${props.theme.buttons.primary.color};
  `}
`;
const secondaryButtonStyles = css `
  background-color: ${(props) => props.theme.buttons.secondary.backgroundColor};
  color: ${(props) => props.theme.buttons.secondary.color};

  ${(props) => hoverFocus `
    background-color: ${props.theme.buttons.secondary.hoverFocusBackgroundColor};
  `}
`;
const darkButtonStyles = css `
  background-color: ${(props) => props.theme.buttons.dark.backgroundColor};
  color: ${(props) => props.theme.buttons.dark.color};

  /* Only the dark button style has a bold weight, unlike the others  */
  font-weight: ${(props) => props.theme.fonts.weights.bold};

  ${(props) => hoverFocus `
    background-color: ${props.theme.buttons.dark.hoverFocusBackgroundColor};
  `}
`;
const lightButtonStyles = css `
  background-color: ${(props) => props.theme.buttons.light.backgroundColor};
  color: ${(props) => props.theme.buttons.light.color};

  ${(props) => hoverFocus `
    background-color: ${props.theme.buttons.light.hoverFocusBackgroundColor};
  `}
  ${(props) => props.theme.buttons.light.border && `border: ${props.theme.buttons.light.border};`}
  ${(props) => props.theme.buttons.light.fontWeight && `font-weight: ${props.theme.buttons.light.fontWeight};`}
`;
const selectedButtonStyles = css `
  background-color: ${(props) => props.theme.colors.green};
  color: ${(props) => props.theme.colors.white};
  ${(props) => hoverFocus `
    background-color: ${props.theme.colors.greenD30};
  `}
`;
const linkButtonStyles = css `
  box-shadow: none;
  width: auto;
  min-width: auto;
  height: auto;
  padding: 0 !important;
  background-color: transparent;
  border-color: transparent;
  ${(props) => props.theme.fonts.foundations.desktop.body}
  text-align: left;
  white-space: normal;
  font-weight: ${(props) => props.theme.buttons.linkFontWeight || 'normal'};
  text-decoration: ${(props) => props.theme.buttons.textDecoration || 'underline'};

  ${(props) => hoverFocus `
  text-decoration: ${props.theme.buttons.hoverTextDecoration || 'inherit'};
  `}
`;
//# sourceMappingURL=button.styles.js.map